import { useState } from "react";
import { Share2, Copy, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/lib/api";

export const ShareDialog = ({ api, doc, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const url = doc.share_id ? `${window.location.origin}/r/${doc.share_id}` : "";

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/share`, { enabled });
      onChange(data);
      toast.success(enabled ? "Public link is on" : "Public link turned off");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed. Select the link and copy it manually."); }
  };

  return (
    <>
      <button className="btn-ink" onClick={() => setOpen(true)} data-testid="share-btn"><Share2 className="h-4 w-4 text-thread" /> Share</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-paper-border bg-paper" data-testid="share-dialog">
          <DialogHeader>
            <p className="eyebrow">Public link</p>
            <DialogTitle className="font-serif text-2xl font-medium">Share this resume</DialogTitle>
            <DialogDescription>Anyone with the link can view it, including your contact details. Turn it off any time.</DialogDescription>
          </DialogHeader>
          <label className="flex items-center justify-between rounded-lg border border-paper-border bg-white p-4">
            <span className="text-sm font-semibold text-ink">Public link {doc.shared ? "on" : "off"}</span>
            <span className="flex items-center gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin text-thread" />}
              <Switch checked={!!doc.shared} onCheckedChange={toggle} disabled={busy} data-testid="share-toggle" /></span>
          </label>
          {doc.shared && url && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input readOnly value={url} className="field font-mono text-xs" onFocus={(e) => e.target.select()} data-testid="share-url-input" />
                <button className="btn-thread !px-3" onClick={copy} data-testid="share-copy-btn"><Copy className="h-4 w-4" /></button>
                <a className="btn-ink !px-3" href={url} target="_blank" rel="noreferrer" data-testid="share-open-link"><ExternalLink className="h-4 w-4" /></a>
              </div>
              <p className="text-xs text-ink-soft" data-testid="share-status-note">
                {doc.can_download ? "Recruiters see a clean resume and can download the PDF." : "This resume isn't unlocked yet, so recruiters see the \"Made with Pitchpaper\" watermark. Unlock it to share a clean version they can download."}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
